<template>
  <div>
    <div>
      <div class="mb-3">
        <label for="timeSelect" class="form-label">Select Time Extension</label>
        <select class="form-select" v-model="selectedTime" @change="calculateCost" id="amount">
          <option v-for="option in timeOptions" :key="option.value" :value="option.cost">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="row mb-3">
        <div class="form-check col-md-4 align-self-end ms-3">
          <input class="form-check-input" type="checkbox" v-model="addPagePin" id="addPagePin">
          <label class="form-check-label form-label" for="addPagePin">
            Add Page PIN?
          </label>
        </div>
        <div class="col-md-6">
          <input type="text" class="form-control" v-model="pin" :disabled="!addPagePin" placeholder="Enter PIN">
        </div>
        <div class="row mb-3">
          <div class="col-md-12">
            <label for="email" class="form-label">Email</label>
            <input type="email" class="form-control" v-model="userEmail" id="userEmail" placeholder="Enter your email">
          </div>
        </div>
      </div>

      <div class="mb-3">
        <strong>Total Charge:</strong> <span>{{ totalCharge }}</span>
      </div>

      <button type="button" class="btn btn-success w-100" :disabled="checkoutLoading" @click="processPayment">
        {{ checkoutLoading ? 'Redirecting…' : 'Process Card' }}
      </button>
    </div>

    <!--
    ========== Chingup iframe (replaced by Stripe Checkout) ==========
    <div v-if="!isIframeVisible"> ... </div>
    <iframe
      title="Payment Form"
      allowfullscreen
      @load="onIframeLoad"
      ref="myIframe"
      id="payment_iframe"
      :src="iframeSrc"
      style="width:100%; height:500px;" frameborder="0"
      v-show="isIframeVisible"
    ></iframe>
    -->
  </div>
</template>

<script>
import api from '../api.js';

const STRIPE_PENDING_KEY = 'pyast_stripe_pending';

export default {
  props: {
    uuidCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedTime: 1.0,
      addPagePin: false,
      pin: '',
      totalCharge: '$1',
      selectedDays: 12,
      userEmail: '',
      checkoutLoading: false,
      timeOptions: [
        { label: '12 days = $1', days: 12, cost: 1.0 },
        { label: '30 days = $2.40', days: 30, cost: 2.4 },
        { label: '3 months = $7.20', days: 90, cost: 7.2 },
        { label: '6 months = $14.60', days: 180, cost: 14.6 },
        { label: '1 year = $29.20', days: 365, cost: 29.2 },
      ],
      /*
      // Chingup iframe state
      isIframeVisible: false,
      iframeSrc: '',
      lastPaymentData: null,
      paymentDataSent: false,
      isIframeLoaded: false,
      */
    };
  },
  /*
  mounted() {
    window.addEventListener('message', async (event) => {
      console.log('Message received from iframe:', event.data);
      if (event.data?.type === 'iframePing') {
        this.isIframeLoaded = true;
        this.sendPaymentData();
        return;
      }
      var response = event.data;
      if (response && typeof response === 'object' && 'success' in response) {
        if (response.success) {
          alert(`Payment processed successfully!`);
          await this.afterPaymentUpdateStatus();
        } else {
          let errorMsg = `${response.message || 'Payment failed'}\n`;
          if (response.errors && typeof response.errors === 'object' && Object.keys(response.errors).length) {
            for (let key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                if (Array.isArray(response.errors[key])) {
                  errorMsg += `  ${key}: ${response.errors[key].join(', ')}\n`;
                } else if (typeof response.errors[key] === 'object') {
                  errorMsg += `  ${key}: ${JSON.stringify(response.errors[key])}\n`;
                } else {
                  errorMsg += `  ${key}: ${response.errors[key]}\n`;
                }
              }
            }
          } else {
            errorMsg += 'No additional errors.';
          }
          alert(errorMsg);
        }
      }
    });
  },
  */
  emits: ['close-modal'],
  methods: {
    calculateCost() {
      this.totalCharge = `$${this.selectedTime}`;
    },

    getStripePublishableKey() {
      const k = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      return typeof k === 'string' ? k.trim() : '';
    },

    async ensureStripeJs() {
      if (window.Stripe) {
        return;
      }
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://js.stripe.com/v3/';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load Stripe.js'));
        document.head.appendChild(s);
      });
    },

    async processPayment() {
      if (this.addPagePin && this.pin === '') {
        alert('Pin is required.');
        return;
      }
      if (this.userEmail === '') {
        alert('Email is required.');
        return;
      }

      const pk = this.getStripePublishableKey();
      if (!pk) {
        alert('Stripe is not configured. Add VITE_STRIPE_PUBLISHABLE_KEY to your .env file.');
        return;
      }

      const selectedOption = this.timeOptions.find(
        (option) => option.cost === parseFloat(this.selectedTime)
      );
      const extendDays = selectedOption ? selectedOption.days : 12;

      this.checkoutLoading = true;
      try {
        const formData = new FormData();
        formData.append('extend_days', String(extendDays));
        formData.append('uuid_code', this.uuidCode);
        formData.append('email', this.userEmail);
        formData.append('isSetPin', this.addPagePin ? 'true' : 'false');

        const { data } = await api.post('stripe_checkout_create.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (!data.success || !data.sessionId) {
          alert(data.message || 'Could not start checkout.');
          return;
        }

        sessionStorage.setItem(
          STRIPE_PENDING_KEY,
          JSON.stringify({
            uuidCode: this.uuidCode,
            pin: this.pin || '',
            addPagePin: this.addPagePin,
          })
        );

        await this.ensureStripeJs();
        const stripe = window.Stripe(pk);
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) {
          alert(error.message || 'Could not redirect to Stripe Checkout.');
        }
      } catch (err) {
        console.error(err);
        alert('Something went wrong. Please try later.');
      } finally {
        this.checkoutLoading = false;
      }
    },

    modalClosed() {
      this.checkoutLoading = false;
    },

    /*
    async onIframeLoad() {
      var iframe = document.getElementById('payment_iframe');
      let amount = parseFloat(this.selectedTime);
      var uniqId = this.generateUniqueNumber();
      iframe.onload = () => {
        const css_config = {};
        this.lastPaymentData = {
          amount: amount,
          merchant_id: '89fdf4de0e3cf9a00a613ef031e2d9ba',
          type: 'paymentData',
          transaction_id: uniqId,
          css_config: css_config,
          require_billing_info: false,
        };
        this.paymentDataSent = false;
        this.isIframeLoaded = false;
        this.sendPaymentData();
        iframe.style.display = 'block';
      };
    },
    sendPaymentData() {
      var iframe = document.getElementById('payment_iframe');
      if (iframe?.contentWindow && this.lastPaymentData && !this.paymentDataSent && this.isIframeLoaded) {
        const plainData = JSON.parse(JSON.stringify(this.lastPaymentData));
        iframe.contentWindow.postMessage(plainData, '*');
        this.paymentDataSent = true;
      }
    },
    async processPaymentChingup() {
      if (this.addPagePin && this.pin == '') {
        alert('Pin is required.');
      } else if (this.userEmail == '') {
        alert('Email is required.');
      } else {
        try {
          this.iframeSrc = 'about:blank';
          setTimeout(() => {
            this.iframeSrc = 'https://chingup.com/card_payment/index.html';
            this.isIframeVisible = true;
          }, 100);
        } catch (error) {
          console.error('Error:', error);
          alert('Something went wrong. Please try later.');
        }
      }
    },
    async afterPaymentUpdateStatus() {
      try {
        const selectedOption = this.timeOptions.find((option) => option.cost === parseFloat(this.selectedTime));
        if (selectedOption) {
          this.selectedDays = selectedOption.days;
        }
        var formData = new FormData();
        formData.append('extend_day', this.selectedDays);
        formData.append('uuid_code', this.uuidCode);
        formData.append('isSetPin', this.addPagePin);
        formData.append('pin', this.pin);
        formData.append('email', this.userEmail);
        const response = await api.post('extendtime.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data.success) {
          alert(response.data.message);
          window.location.reload();
        } else {
          alert(response.data.message);
          window.location.reload();
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Something went wrong. Please try later.');
      }
    },
    generateUniqueNumber() {
      const timestamp = Date.now().toString();
      const randomNumber = Math.floor(Math.random() * 900) + 100;
      return timestamp + randomNumber;
    },
    */
  },
};
</script>
